<script setup>
// custom (redesign Modeloja · F2): macOS-style segmented period control
// (Hoje · 7 dias · 30 dias · 12 meses). Active = elevated surface; inactive = transparent +
// gray text. Never a black-filled active. v-model on the selected option string.
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => ['Hoje', '7 dias', '30 dias', '12 meses'],
  },
  modelValue: {
    type: String,
    default: '7 dias',
  },
  ariaLabel: {
    type: String,
    default: 'Período',
  },
});

const emit = defineEmits(['update:modelValue']);

const buttons = ref([]);

// ══ A PÍLULA QUE DESLIZA ════════════════════════════════════════════════════════════════
// Antes, trocar de opção acendia um fundo numa e apagava na outra — dois eventos separados,
// sem nada ligando um ao outro. O controle segmentado da Apple tem UMA pílula que se MOVE, e
// a diferença não é decorativa: o movimento é o que diz que as opções são a mesma coisa em
// estados diferentes, e não três botões independentes. Também é o que faz a seleção parecer
// resposta ao seu gesto em vez de um redesenho da tela.
//
// A posição é MEDIDA, não calculada: as opções têm larguras diferentes ("Dia" e "12 meses"),
// então dividir a barra em partes iguais colocaria a pílula fora do rótulo. `offsetLeft` e
// `offsetWidth` do botão ativo são a única fonte que não mente sobre isso.
const pilula = ref({ x: 0, largura: 0, pronta: false });

function medirPilula() {
  const i = props.options.indexOf(props.modelValue);
  const alvo = buttons.value[i];
  if (!alvo) return;
  pilula.value = {
    x: alvo.offsetLeft,
    largura: alvo.offsetWidth,
    pronta: true,
  };
}

// `pronta` existe para o PRIMEIRO quadro: sem ela a pílula nasceria em x=0 com largura 0 e
// deslizaria até o lugar certo na abertura da tela — uma animação que ninguém pediu e que
// chama atenção para um controle que ainda não foi tocado.
const estiloPilula = computed(() => ({
  transform: `translateX(${pilula.value.x}px)`,
  width: `${pilula.value.largura}px`,
  opacity: pilula.value.pronta ? 1 : 0,
}));

watch(
  () => props.modelValue,
  () => nextTick(medirPilula)
);
watch(
  () => props.options,
  () => nextTick(medirPilula),
  { deep: true }
);

// A largura dos rótulos muda com a fonte e com o zoom do navegador; sem observar, a pílula
// fica no lugar de ontem.
let observador = null;
onMounted(() => {
  nextTick(medirPilula);
  if (typeof ResizeObserver !== 'undefined') {
    observador = new ResizeObserver(medirPilula);
    buttons.value.forEach(b => b && observador.observe(b));
  }
});
onUnmounted(() => observador?.disconnect());

const select = option => {
  if (option !== props.modelValue) emit('update:modelValue', option);
};

// Arrow-key roving focus + selection across the tablist.
const onKeydown = (event, index) => {
  const { length } = props.options;
  let next = null;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    next = (index + 1) % length;
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    next = (index - 1 + length) % length;
  } else if (event.key === 'Home') {
    next = 0;
  } else if (event.key === 'End') {
    next = length - 1;
  }
  if (next === null) return;
  event.preventDefault();
  select(props.options[next]);
  buttons.value[next]?.focus();
};
</script>

<template>
  <div
    role="tablist"
    :aria-label="ariaLabel"
    class="mj-seg relative inline-flex p-1 rounded-full bg-n-slate-3"
  >
    <!-- A pílula fica ATRÁS dos rótulos (`z` menor) e não recebe ponteiro: ela é o realce,
         não o alvo. Quem recebe o clique continua sendo o botão. -->
    <span
      class="mj-seg__pilula absolute top-1 bottom-1 left-0 rounded-full pointer-events-none"
      :style="estiloPilula"
      aria-hidden="true"
    />
    <button
      v-for="(option, index) in options"
      :key="option"
      ref="buttons"
      type="button"
      role="tab"
      :aria-selected="option === modelValue"
      :tabindex="option === modelValue ? 0 : -1"
      class="relative z-[1] cursor-pointer whitespace-nowrap rounded-full border-0 bg-transparent px-4 py-[7px] font-sans text-mj-13 font-600 transition-[color,transform] duration-mj-toque ease-mj active:scale-[.96] focus-visible:outline-2 focus-visible:outline-n-slate-12 focus-visible:outline-offset-2 motion-reduce:transition-none motion-reduce:transform-none"
      :class="
        option === modelValue
          ? 'text-n-slate-12'
          : 'text-n-slate-10 hover:text-n-slate-11'
      "
      @click="select(option)"
      @keydown="onKeydown($event, index)"
    >
      {{ option }}
    </button>
  </div>
</template>

<style scoped>
/* A pílula move `transform` e `width`, e só. `left` mudaria o layout a cada quadro; `transform`
   roda no compositor.
   250ms é o teto onde ainda se ACOMPANHA o movimento com os olhos — abaixo disso ele vira um
   pulo e o efeito de "a mesma coisa mudando de estado" se perde, que é justamente o que a
   pílula existe para dizer. */
.mj-seg__pilula {
  background-color: rgb(var(--solid-1));
  box-shadow:
    0 1px 2px rgb(16 24 40 / 6%),
    0 1px 3px rgb(16 24 40 / 10%);
  transition:
    transform 250ms var(--mj-curva),
    width 250ms var(--mj-curva),
    opacity var(--mj-toque) var(--mj-curva);
}

/* No modo reduzido a pílula aparece direto no lugar certo: o realce continua, o deslocamento
   sai. Ela ainda diz QUAL está ativa — que é a informação; o movimento era o encanto. */
@media (prefers-reduced-motion: reduce) {
  .mj-seg__pilula {
    transition: opacity var(--mj-toque) var(--mj-curva);
  }
}
</style>
